import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import { IconBell } from './Icons';

export default function NotificationBell() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const hasPermission = can('manage_admin');

  // Poll unread count every 60s
  const fetchCount = useCallback(async () => {
    if (!hasPermission) return;
    try {
      const data = await api.getComplianceNotificationCount();
      setCount(data.count || 0);
    } catch { /* silent */ }
  }, [hasPermission]);

  // Fetch notifications when dropdown opens
  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getComplianceNotifications({ unread: true, limit: 10 });
      setNotifications(Array.isArray(data) ? data : []);
    } catch {
      setNotifications([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, [fetchCount]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Don't render if user can't manage admin
  if (!hasPermission) return null;

  function toggleOpen() {
    const next = !open;
    setOpen(next);
    if (next) fetchNotifications();
  }

  async function markAllRead() {
    try {
      await api.markAllNotificationsRead();
      setCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: 1 })));
    } catch { /* silent */ }
  }

  async function markRead(id) {
    try {
      await api.markNotificationRead(id);
      setCount(prev => Math.max(0, prev - 1));
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: 1 } : n));
    } catch { /* silent */ }
  }

  function getIcon(type) {
    switch (type) {
      case 'expired': return '\u{1F534}';
      case 'expiring_soon': return '\u{1F7E1}';
      case 'missing_document': return '\u{1F4C4}';
      default: return '\u{1F514}';
    }
  }

  function getLabel(type) {
    switch (type) {
      case 'expired': return 'Expired';
      case 'expiring_soon': return 'Expiring Soon';
      case 'missing_document': return 'Missing Doc';
      default: return 'Alert';
    }
  }

  function formatTime(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'Z');
    const now = new Date();
    const diff = now - d;
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  }

  function handleNotifClick(notif) {
    markRead(notif.id);
    setOpen(false);
    navigate('/admin-mod/dashboard');
  }

  return (
    <div className="notification-bell-wrapper" ref={ref}>
      <button className="notification-bell-btn" onClick={toggleOpen} title="Compliance Notifications">
        <IconBell size={20} />
        {count > 0 && (
          <span className="notification-bell-badge">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div className="notification-dropdown">
          <div className="notification-dropdown-header">
            <span className="notification-dropdown-title">Compliance Alerts</span>
            {count > 0 && (
              <button className="notification-mark-all" onClick={markAllRead}>
                Mark all read
              </button>
            )}
          </div>

          <div className="notification-dropdown-body">
            {loading && <div className="notification-empty">Loading...</div>}
            {!loading && notifications.length === 0 && (
              <div className="notification-empty">No unread notifications</div>
            )}
            {!loading && notifications.map(n => (
              <button
                key={n.id}
                className={`notification-item ${n.is_read ? 'read' : 'unread'}`}
                onClick={() => handleNotifClick(n)}
              >
                <span className="notification-item-icon">{getIcon(n.type)}</span>
                <div className="notification-item-content">
                  <div className="notification-item-title">
                    <span className={`notification-type-badge ${n.type}`}>{getLabel(n.type)}</span>
                    <span className="notification-item-time">{formatTime(n.created_at)}</span>
                  </div>
                  <div className="notification-item-message">{n.message}</div>
                  {n.item_title && (
                    <div className="notification-item-meta">
                      {n.entity_type && <span className="notification-entity-type">{n.entity_type}</span>}
                      {n.item_title}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>

          <div className="notification-dropdown-footer">
            <button onClick={() => { setOpen(false); navigate('/admin-mod/dashboard'); }}>
              View Compliance Dashboard
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
